import { MultiSelectDropdown } from "@/components/MultiSelectDropdown"
import type { MultiSelectOptionGroup } from "@/components/MultiSelectDropdown"
import type { AdsManagerColumn } from "../lib/columns"

interface FilterBarProps {
  userGroups: MultiSelectOptionGroup<number>[]
  selectedUserIds: number[]
  onUserIdsChange: (ids: number[]) => void
  selectedAccountIds: string[]
  onAccountIdsChange: (ids: string[]) => void
  /** Grouped account options for the account filter (active + deleted). */
  accountGroups: MultiSelectOptionGroup<string>[]
  columns: readonly AdsManagerColumn[]
  hiddenColumns: Set<string>
  onToggleColumn: (columnId: string) => void
  /** Inline toolbar row (desktop) vs full-width stacked controls (mobile sheet). */
  layout?: "inline" | "stacked"
}

export function FilterBar({
  userGroups,
  selectedUserIds,
  onUserIdsChange,
  selectedAccountIds,
  onAccountIdsChange,
  accountGroups,
  columns,
  hiddenColumns,
  onToggleColumn,
  layout = "inline",
}: FilterBarProps) {
  const selectedColumnIds = columns
    .filter((c) => !hiddenColumns.has(c.id))
    .map((c) => c.id)

  const handleColumnsChange = (nextSelectedIds: string[]) => {
    const prevSet = new Set(selectedColumnIds)
    const nextSet = new Set(nextSelectedIds)
    columns.forEach((col) => {
      const wasSelected = prevSet.has(col.id)
      const isSelected = nextSet.has(col.id)
      if (wasSelected !== isSelected) {
        onToggleColumn(col.id)
      }
    })
  }

  const isStacked = layout === "stacked"
  const controlWidth = isStacked ? "100%" : undefined

  const hasUsers = userGroups.some((group) => group.options.length > 0)

  return (
    <div
      className={
        isStacked
          ? "flex w-full flex-col gap-3"
          : "inline-flex min-w-max shrink-0 flex-nowrap items-center gap-1.5"
      }
    >
      {accountGroups.length > 0 && (
        <div className={isStacked ? "space-y-1" : undefined}>
          {isStacked && (
            <span className="text-[11px] font-semibold uppercase tracking-wider text-[#b0b0b0]">
              Accounts
            </span>
          )}
          <MultiSelectDropdown
            label="Accounts"
            optionGroups={accountGroups}
            selectedIds={selectedAccountIds}
            onChange={onAccountIdsChange}
            className={isStacked ? "w-full" : "shrink-0"}
            minWidth={controlWidth ?? "145px"}
            dropdownMinWidth="280px"
            searchable
            searchPlaceholder="Search account..."
            showApplyClearActions
          />
        </div>
      )}

      {hasUsers && (
        <div className={isStacked ? "space-y-1" : undefined}>
          {isStacked && (
            <span className="text-[11px] font-semibold uppercase tracking-wider text-[#b0b0b0]">
              Users
            </span>
          )}
          <MultiSelectDropdown
            label="Users"
            optionGroups={userGroups}
            selectedIds={selectedUserIds}
            onChange={onUserIdsChange}
            className={isStacked ? "w-full" : "shrink-0"}
            minWidth={controlWidth ?? "125px"}
            dropdownMinWidth="240px"
            searchable
            searchPlaceholder="Search users..."
            showApplyClearActions
          />
        </div>
      )}

      <div className={isStacked ? "space-y-1" : undefined}>
        {isStacked && (
          <span className="text-[11px] font-semibold uppercase tracking-wider text-[#b0b0b0]">
            Columns
          </span>
        )}
        <MultiSelectDropdown
          label="Columns"
          alwaysShowLabel
          options={columns.map((col) => ({ id: col.id, label: col.label }))}
          selectedIds={selectedColumnIds}
          onChange={handleColumnsChange}
          className={isStacked ? "w-full" : "shrink-0"}
          minWidth={controlWidth ?? "110px"}
        />
      </div>
    </div>
  )
}
